import Guid from "_shared/guids/guid";
import _ from "lodash";
import ProposalField, { HumanReadableProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import TextChange, { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import { RedlineAction, RedlineChange } from "work/proposals/redlining/changes/redline-change";
import FieldRedlineSchema, {
  FieldRedlineArrayJSON,
  FieldRedlineJSON,
  FieldRedlineParams,
} from "work/proposals/redlining/field-redlines/_schemas/field-redline-schema";

export default class FieldRedline<T extends IRedlineableField> {
  public static fromJSON<T extends IRedlineableField>(params: FieldRedlineJSON, field: ProposalField): FieldRedline<T> {
    return new FieldRedline<T>({ ...FieldRedlineSchema.deserialize(params, field), field });
  }

  public readonly textChangeCollection: TextChangeCollection | undefined;
  public readonly field: ProposalField;
  public readonly originalEntry: T | null;
  public readonly revisedEntry: T | null;
  public readonly currentEntry: T | null | undefined;
  public readonly diffWords: boolean;

  public get textChanges(): TextChange[] | undefined {
    return this.textChangeCollection?.textChanges;
  }
  public get label(): string {
    return HumanReadableProposalFieldName[this.field.name];
  }
  public get sessionHistory(): RedlineChange[] {
    return [...this._sessionHistory];
  }
  public get isRevised(): boolean {
    return this.currentEntry !== undefined && !this.isAccepted;
  }
  public get isNewlyRevised(): boolean {
    return Boolean(this.currentEntry && !this.revisedEntry?.isEqualTo(this.currentEntry));
  }
  public get wasRevised(): boolean {
    const originalEmpty = !this.originalEntry || this.originalEntry.isEmpty;
    const revisedEmpty = !this.revisedEntry || this.revisedEntry.isEmpty;

    if (originalEmpty && revisedEmpty) return false;
    if (originalEmpty || revisedEmpty) return true;
    return !this.revisedEntry.isEqualTo(this.originalEntry);
  }
  public get isAccepted(): boolean {
    return (
      (this.currentEntry === null && this.revisedEntry === null) || !!this.currentEntry?.isEqualTo(this.revisedEntry)
    );
  }
  public get isRejected(): boolean {
    return this.isResolved && this.wasRevised && !this.isAccepted;
  }
  public get isResolved(): boolean {
    return this.currentEntry !== undefined;
  }
  public get canBeUndone(): boolean {
    return this.hasChanges || (this.isResolved && this.wasRevised) || this.hasPartiallyResolvedTextChanges;
  }
  public get hasChanges(): boolean {
    return this.isNewlyAdded || this.isNewlyRemoved || this.isNewlyRevised || this.isRejected;
  }
  public get isAdded(): boolean {
    return (
      (this.originalEntry === null && this.revisedEntry !== null) || (this.revisedEntry === null && !!this.currentEntry)
    );
  }
  public get isNewlyAdded(): boolean {
    return Boolean(this.currentEntry && !this.wasRevised && !this.revisedEntry);
  }
  public get wasAdded(): boolean {
    return this.originalEntry === null && this.revisedEntry !== null;
  }
  public get isRemoved(): boolean {
    if (this.currentEntry) return false;
    return (
      (this.originalEntry !== null && this.currentEntry === null) ||
      (this.originalEntry !== null && this.revisedEntry === null && this.currentEntry === undefined) ||
      (this.revisedEntry !== null && this.currentEntry === null)
    );
  }
  public get isNewlyRemoved(): boolean {
    return this.revisedEntry !== null && this.currentEntry === null;
  }
  public get wasRemoved(): boolean {
    return this.originalEntry !== null && this.revisedEntry === null;
  }

  constructor(params: FieldRedlineParams & { field: ProposalField; previousRedline?: FieldRedline<T> }) {
    const {
      field,
      textChangeCollection,
      sessionHistory,
      latestAction,
      previousRedline,
      originalEntry,
      revisedEntry,
      currentEntry,
    } = params;

    const diffWords = params.diffWords ?? false;

    this.field = field;
    this.originalEntry = originalEntry;
    this.revisedEntry = revisedEntry;
    let entryToAssign: T | null | undefined;
    if (currentEntry !== undefined) {
      entryToAssign = currentEntry;
    } else if (
      (!revisedEntry && !originalEntry) ||
      (revisedEntry?.isEmpty && originalEntry?.isEmpty) ||
      revisedEntry?.isEqualTo(originalEntry)
    ) {
      entryToAssign = revisedEntry;
    } else {
      entryToAssign = currentEntry;
    }
    this.currentEntry = entryToAssign;

    this.diffWords = diffWords;
    this.textChangeCollection =
      textChangeCollection ??
      TextChangeCollection.fromRedlines(
        {
          originalEntry,
          revisedEntry,
          currentEntry,
        },
        { diffWords },
      );

    this._sessionHistory = sessionHistory ?? [];
    if (!this.canBeUndone || this._sessionHistory.length > 0) return;

    let action: RedlineAction = this.determineRedlineChangeAction(latestAction, previousRedline);

    if (!this.textChanges?.length) return;

    this._sessionHistory.push(
      new RedlineChange({
        field: this.field,
        isResolved: this.isResolved,
        action: action,
        textChanges: this.textChanges,
      }),
    );
  }

  public toJSON(): FieldRedlineJSON {
    return FieldRedlineSchema.serialize({
      ...this,
      sessionHistory: this.sessionHistory,
      diffWords: this.diffWords,
    });
  }

  public add(newEntry: T): FieldRedline<T> {
    return new FieldRedline({
      ...this,
      latestAction: RedlineAction.Add,
      currentEntry: newEntry,
      diffWords: this.diffWords,
      textChangeCollection: null,
    });
  }

  public edit(newEntry: T | null): FieldRedline<T> {
    if (newEntry === this.currentEntry) {
      console.warn("No changes detected when editing redline.", this, newEntry);
      return this;
    }

    return new FieldRedline({
      ...this,
      latestAction: RedlineAction.Edit,
      diffWords: this.diffWords,
      currentEntry: newEntry,
      textChangeCollection: null,
    });
  }

  public remove(): FieldRedline<T> {
    if (this.currentEntry === null) {
      console.warn("Can't remove a redline that hasn't been modified");
      return this;
    }
    return new FieldRedline({
      ...this,
      latestAction: RedlineAction.Remove,
      diffWords: this.diffWords,
      currentEntry: null,
      textChangeCollection: null,
    });
  }

  public accept(): FieldRedline<T> {
    return new FieldRedline({
      ...this,
      diffWords: this.diffWords,
      latestAction: RedlineAction.Accept,
      currentEntry: this.revisedEntry,
      textChangeCollection: null,
    });
  }

  public reject(): FieldRedline<T> {
    return new FieldRedline({
      ...this,
      diffWords: this.diffWords,
      latestAction: RedlineAction.Reject,
      currentEntry: this.originalEntry,
      textChangeCollection: null,
    });
  }

  public undo(): FieldRedline<T> {
    if (this.currentEntry === undefined) {
      if (!this.hasPartiallyResolvedTextChanges) {
        console.warn("Can't undo a redline that hasn't been modified");
        return this;
      }

      return new FieldRedline<T>({
        ...this,
        diffWords: this.diffWords,
        latestAction: RedlineAction.Undo,
        previousRedline: this,
        currentEntry: undefined,
        textChangeCollection: null,
      });
    }

    let currentEntry: T | null | undefined;
    if (!this.wasRevised) {
      currentEntry = this.originalEntry;
    }

    return new FieldRedline<T>({
      ...this,
      diffWords: this.diffWords,
      latestAction: RedlineAction.Undo,
      previousRedline: this,
      currentEntry,
      textChangeCollection: null,
    });
  }

  public isEqualTo(other?: FieldRedline<T>): boolean {
    return Boolean(
      other &&
      this.field.isEqualTo(other.field) &&
      ((this.originalEntry === null && other.originalEntry === null) ||
        this.originalEntry?.isEqualTo(other.originalEntry)) &&
      ((this.revisedEntry === null && other.revisedEntry === null) ||
        this.revisedEntry?.isEqualTo(other.revisedEntry)) &&
      ((this.currentEntry === null && other.currentEntry === null) ||
        (this.currentEntry === undefined && other.currentEntry === undefined) ||
        this.currentEntry?.isEqualTo(other.currentEntry)),
    );
  }

  public isCurrentWith(other: FieldRedline<T>): boolean {
    if (!this.field.isEqualTo(other.field)) return false;
    if (this.originalEntry === null && other.originalEntry !== null) return false;
    if (this.originalEntry && !this.originalEntry.isEqualTo(other.originalEntry)) return false;
    if (this.revisedEntry === null && other.revisedEntry !== null) return false;
    return !(this.revisedEntry && !this.revisedEntry.isEqualTo(other.revisedEntry));
  }

  protected readonly _sessionHistory: RedlineChange[] = [];

  private get hasPartiallyResolvedTextChanges(): boolean {
    if (this.currentEntry !== undefined || !this.textChanges?.length) return false;

    const baselineTextChanges = TextChangeCollection.fromRedlines(
      {
        originalEntry: this.originalEntry,
        revisedEntry: this.revisedEntry,
        currentEntry: this.currentEntry,
      },
      { diffWords: this.diffWords },
    )?.textChanges;

    if (!baselineTextChanges?.length) return false;

    return !_.isEqual(
      this.textChanges.map((change) => change.diff),
      baselineTextChanges.map((change) => change.diff),
    );
  }

  private determineRedlineChangeAction(
    latestAction: RedlineAction | undefined,
    previousRedline: FieldRedline<T> | undefined,
  ): RedlineAction {
    let action = latestAction;
    if (latestAction?.isEqualTo(RedlineAction.Edit)) {
      action = this.determineEditChangeAction(latestAction);
    } else if (latestAction?.isEqualTo(RedlineAction.Undo)) {
      action = this.determineUndoChangeAction(previousRedline, latestAction);
    }
    if (action) {
      return action;
    }
    return previousRedline ? RedlineAction.Edit : RedlineAction.Add;
  }

  private determineEditChangeAction(action: RedlineAction): RedlineAction {
    if (this.isNewlyAdded) {
      action = RedlineAction.Add;
    } else if (this.isNewlyRemoved) {
      action = RedlineAction.Remove;
    } else if (this.wasRevised && this.isNewlyRevised) {
      action = RedlineAction.ReEdit;
    }
    return action;
  }

  private determineUndoChangeAction(
    previousRedline: FieldRedline<T> | undefined,
    action: RedlineAction,
  ): RedlineAction {
    if (previousRedline?.wasRevised && !previousRedline.isNewlyRevised && previousRedline.currentEntry) {
      action = RedlineAction.Undo;
    } else if (previousRedline?.wasRevised && previousRedline.isAdded && this.isNewlyRemoved) {
      action = RedlineAction.UndoAdd;
    } else if (previousRedline?.isRemoved && !this.isRemoved) {
      action = RedlineAction.UndoRemove;
    } else if (previousRedline?.wasRevised && previousRedline.isAccepted) {
      action = RedlineAction.UndoAccept;
    } else if (previousRedline?.wasRevised && previousRedline.isRejected) {
      action = RedlineAction.UndoReject;
    } else if (
      (previousRedline?.isResolved &&
        previousRedline.currentEntry &&
        !previousRedline.currentEntry.isEmpty &&
        this.currentEntry == null) ||
      this.currentEntry?.isEmpty
    ) {
      action = RedlineAction.Remove;
    } else if (this.isResolved) {
      action = RedlineAction.UndoEdit;
    }
    return action;
  }
}

export class FieldRedlineArray<T extends IComparableFieldEntryWithId> {
  public static fromJSON<T extends IComparableFieldEntryWithId>(
    jsonArray: FieldRedlineArrayJSON,
    field: ProposalField,
    entryField: (id: Guid) => ProposalField,
  ): FieldRedlineArray<T> {
    const fieldRedlineArrayParams = FieldRedlineSchema.deserializeArray(jsonArray, field);
    return new FieldRedlineArray({
      field,
      entryField,
      redlines: fieldRedlineArrayParams.redlines.map((redline) => {
        const id = redline.currentEntry?.id ?? redline.revisedEntry?.id ?? redline.originalEntry?.id;
        return new FieldRedline<T>({ ...redline, field: entryField(id) });
      }),
      sessionHistory: fieldRedlineArrayParams.sessionHistory.map((change) => {
        return new RedlineChange({ ...change, field });
      }),
    });
  }

  public static fromRedlines<T extends IComparableFieldEntryWithId>(params: {
    field: ProposalField;
    entryField: (id: Guid) => ProposalField;
    redlines: FieldRedline<T>[];
  }): FieldRedlineArray<T> {
    const { field, entryField, redlines } = params;
    return new FieldRedlineArray<T>({
      field,
      entryField,
      redlines,
    });
  }

  public static create<T extends IComparableFieldEntryWithId>(params: {
    field: ProposalField;
    entryField: (id: Guid) => ProposalField;
    originalEntries: T[];
    revisedEntries: T[];
    currentEntries?: T[];
    sessionHistory?: RedlineChange[];
    options?: { diffWords?: boolean };
  }) {
    return new FieldRedlineArray<T>(params);
  }

  public readonly field: ProposalField;
  public readonly entryField: (id: Guid) => ProposalField;
  public readonly diffWords: boolean;

  public get templateIds(): Guid[] {
    return [...this._templateIds];
  }

  public get redlines(): FieldRedline<T>[] {
    return this._redlines;
  }

  public get sessionHistory(): RedlineChange[] {
    return [...this._sessionHistory, ...this._redlines.flatMap((redline) => redline.sessionHistory)];
  }

  public get isRevised(): boolean {
    return this._redlines.some((redline) => redline.isRevised);
  }

  public get isResolved(): boolean {
    return this._redlines.every((redline) => redline.isResolved);
  }

  public get canBeUndone(): boolean {
    return this._redlines.some((redline) => redline.canBeUndone);
  }

  public get isEmpty(): boolean {
    return this._redlines.every((redline) => redline.currentEntry === null);
  }

  public get currentIds(): Guid[] {
    return this._redlines.map((redline) => redline.currentEntry?.id).filter((id) => id !== undefined);
  }
  public get currentEntries(): T[] {
    return this._redlines
      .map((redline) => redline.currentEntry)
      .filter((entry): entry is T => entry !== null && entry !== undefined);
  }

  public get hasRejections(): boolean {
    return this._redlines.some((redline) => redline.isRejected);
  }

  public get hasChanges(): boolean {
    for (const redline of this._redlines) {
      if (redline.hasChanges) return true;
    }
    return false;
  }

  protected constructor(params: {
    field: ProposalField;
    entryField: (id: Guid) => ProposalField;
    originalEntries?: T[];
    revisedEntries?: T[];
    currentEntries?: T[];
    redlines?: FieldRedline<T>[];
    sessionHistory?: RedlineChange[];
    options?: { diffWords?: boolean };
  }) {
    const {
      field,
      entryField,
      originalEntries,
      revisedEntries,
      currentEntries,
      redlines,
      sessionHistory,
      options,
    } = params;
    this.field = field;
    this.entryField = entryField;
    this.diffWords = options?.diffWords ?? false;
    this._sessionHistory = sessionHistory ?? [];
    if (redlines) {
      this._redlines = redlines;
      return;
    } else if (!originalEntries || !revisedEntries) {
      throw new Error("Either redlines or original and revised entries must be provided");
    }

    for (const originalEntry of originalEntries) {
      let fieldRedline: FieldRedline<T> | undefined;
      const hasCurrentEntries = currentEntries !== undefined;

      const revisedEntry =
        revisedEntries.find(
          (entry: T) => entry.id?.isEqualTo(originalEntry.id) || entry.replacesId?.isEqualTo(originalEntry.id),
        ) ?? null;

      const currentEntry =
        currentEntries?.find(
          (entry: T) => entry.id?.isEqualTo(originalEntry.id) || entry.replacesId?.isEqualTo(originalEntry.id),
        ) ?? (hasCurrentEntries ? null : undefined);

      const id = currentEntry?.id ?? revisedEntry?.id ?? originalEntry?.id;
      const itemField = id ? this.entryField(id) : this.field;
      fieldRedline = new FieldRedline<T>({
        field: itemField,
        originalEntry,
        revisedEntry,
        currentEntry,
        diffWords: params.options?.diffWords ?? false,
        textChangeCollection: undefined,
      });
      this._redlines.push(fieldRedline);
    }

    const addedEntriesInRevision = revisedEntries.filter(
      (revisedEntry) =>
        !originalEntries.some(
          (originalEntry) =>
            revisedEntry.id?.isEqualTo(originalEntry.id) || revisedEntry.replacesId?.isEqualTo(originalEntry.id),
        ),
    );
    for (const revisedEntry of addedEntriesInRevision) {
      const currentEntry = currentEntries?.find(
        (currentEntry: T) =>
          currentEntry.id?.isEqualTo(revisedEntry.id) || currentEntry.replacesId?.isEqualTo(revisedEntry.id),
      );
      const id = currentEntry?.id ?? revisedEntry?.id ?? null;
      const itemField = id ? this.entryField(id) : this.field;
      this._redlines.push(
        new FieldRedline<T>({
          originalEntry: null,
          revisedEntry,
          currentEntry,
          field: itemField,
          diffWords: params.options?.diffWords ?? false,
          textChangeCollection: undefined,
        }),
      );
    }

    const addedEntriesInCurrent = currentEntries
      ? currentEntries.filter(
          (currentEntry) =>
            !originalEntries.some(
              (originalEntry) =>
                currentEntry.id?.isEqualTo(originalEntry.id) || currentEntry.replacesId?.isEqualTo(originalEntry.id),
            ) &&
            !revisedEntries.some(
              (revisedEntry) =>
                currentEntry.id?.isEqualTo(revisedEntry.id) || currentEntry.replacesId?.isEqualTo(revisedEntry.id),
            ),
        )
      : [];

    for (const currentEntry of addedEntriesInCurrent) {
      const id = currentEntry?.id ?? null;
      const itemField = id ? this.entryField(id) : this.field;
      this._redlines.push(
        new FieldRedline<T>({
          field: itemField,
          originalEntry: null,
          revisedEntry: null,
          currentEntry,
          diffWords: params.options?.diffWords ?? false,
        }),
      );
    }
  }

  public toJSON() {
    return {
      redlines: this._redlines.map((redline) => redline.toJSON()),
      sessionHistory: this._sessionHistory.map((change) => change.toJSON()),
    };
  }

  public addEntry(entry: T): FieldRedlineArray<T> {
    const sessionHistory = this._sessionHistory.filter((change) => !change.field?.isEqualTo(this.field));

    const existingFieldRedline = this._redlines.find(
      (redline) => redline.revisedEntry?.id.isEqualTo(entry.id) || redline.originalEntry?.id.isEqualTo(entry.id),
    );
    if (!existingFieldRedline) {
      const id = entry?.id ?? null;
      const itemField = id ? this.entryField(id) : this.field;
      const newFieldRedline = new FieldRedline<T>({
        field: itemField,
        originalEntry: null,
        revisedEntry: null,
        currentEntry: entry,
        diffWords: this.diffWords,
      }).add(entry);
      return new FieldRedlineArray<T>({
        field: this.field,
        entryField: this.entryField,
        redlines: [...this._redlines, newFieldRedline],
        sessionHistory,
      });
    }

    const existingFieldRedlineIndex = this._redlines.indexOf(existingFieldRedline);

    if (
      (!existingFieldRedline.revisedEntry && existingFieldRedline.originalEntry) ||
      (existingFieldRedline.revisedEntry && !existingFieldRedline.currentEntry)
    ) {
      // Entry was removed and added again

      const revertedRedline = (this._redlines[existingFieldRedlineIndex] = existingFieldRedline.undo());
      const redlines = [...this._redlines];
      redlines[existingFieldRedlineIndex] = revertedRedline;

      return new FieldRedlineArray<T>({
        field: this.field,
        entryField: this.entryField,
        redlines,
        sessionHistory,
      });
    }

    if (existingFieldRedline?.revisedEntry) {
      // Entry addition was requested in redline
      const acceptedRedline = existingFieldRedline.accept();
      const redlines = [...this._redlines];
      redlines[existingFieldRedlineIndex] = acceptedRedline;
      return new FieldRedlineArray<T>({
        field: this.field,
        entryField: this.entryField,
        redlines,
        sessionHistory,
      });
    }

    if (existingFieldRedline.originalEntry) {
      // Entry removal was requested in redline
      const rejectedRedline = existingFieldRedline.reject();
      const redlines = [...this._redlines];
      redlines[existingFieldRedlineIndex] = rejectedRedline;
      return new FieldRedlineArray<T>({
        field: this.field,
        entryField: this.entryField,
        redlines,
        sessionHistory,
      });
    }

    throw new Error("Unable to add entry to redline array.");
  }

  public replaceEntryById(fieldId: Guid, newEntry: T): FieldRedlineArray<T> {
    if (!fieldId) throw new Error("No id provided");
    const sessionHistory = this._sessionHistory.filter((change) => !change.field?.isEqualTo(this.field));

    const targetIndex = this._redlines.findIndex(
      (fieldRedline) =>
        fieldRedline.currentEntry?.id.isEqualTo(fieldId) ||
        fieldRedline.revisedEntry?.id.isEqualTo(fieldId) ||
        fieldRedline.originalEntry?.id.isEqualTo(fieldId),
    );
    if (targetIndex === -1) throw new Error("Entry not found");
    const targetRedline = this._redlines[targetIndex];
    const newRedlines = [...this._redlines];
    newRedlines[targetIndex] = targetRedline.edit(newEntry);
    return new FieldRedlineArray<T>({
      field: this.field,
      entryField: this.entryField,
      redlines: newRedlines,
      sessionHistory,
    });
  }

  public removeAll(): FieldRedlineArray<T> {
    let newRedlineArray = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id) continue;
      newRedlineArray = newRedlineArray.removeEntryByFieldId(redline.field.id);
    }
    return newRedlineArray;
  }

  public removeEntryByFieldId(fieldId?: Guid): FieldRedlineArray<T> {
    if (!fieldId) throw new Error("No id provided");
    const targetIndex = this._redlines.findIndex((fieldRedline) => fieldRedline.field.id?.isEqualTo(fieldId));
    if (targetIndex === -1) throw new Error("Entry not found");
    const targetFieldRedline = this._redlines[targetIndex];

    const sessionHistory = this._sessionHistory.filter((change) => !change.field?.isEqualTo(this.field));
    const redlines = [...this._redlines];
    if (targetFieldRedline.isNewlyAdded && !targetFieldRedline.originalEntry) {
      redlines.splice(targetIndex, 1);
    } else {
      redlines[targetIndex] = targetFieldRedline.remove();
    }

    return new FieldRedlineArray<T>({
      field: this.field,
      entryField: this.entryField,
      redlines,
      sessionHistory,
    });
  }

  public acceptAll(): FieldRedlineArray<T> {
    let newRedlineArray = this.clone();
    for (const redline of this._redlines) {
      if (!redline.field.id) continue;
      newRedlineArray = newRedlineArray.acceptRedlineById(redline.field.id);
    }
    return newRedlineArray;
  }

  public acceptRedlineById(fieldId: Guid): FieldRedlineArray<T> {
    const newRedline = this.clone();
    const target = newRedline._redlines.find((redline) => redline.field.id?.isEqualTo(fieldId));
    if (target) {
      const index = newRedline._redlines.indexOf(target);
      newRedline._redlines[index] = target.accept();
    }
    return newRedline;
  }

  public rejectAll(): FieldRedlineArray<T> {
    let newRedlineArray = this.clone();
    for (const redline of this._redlines) {
      if (!redline.field.id) continue;
      newRedlineArray = newRedlineArray.rejectRedlineById(redline.field.id);
    }
    return newRedlineArray;
  }

  public rejectRedlineById(fieldId: Guid): FieldRedlineArray<T> {
    const newRedlineArray = this.clone();
    const targetIndex = newRedlineArray._redlines.findIndex((redline) => redline.field.id?.isEqualTo(fieldId));
    if (targetIndex === -1) throw new Error("Redline not found");
    const targetRedline = newRedlineArray._redlines[targetIndex];
    newRedlineArray._redlines[targetIndex] = targetRedline.reject();
    return newRedlineArray;
  }

  public undoAll(): FieldRedlineArray<T> {
    let newRedlineArray = this.clone();
    for (const redline of this._redlines) {
      if (!redline.field.id) continue;
      newRedlineArray = newRedlineArray.undoRedlineById(redline.field.id);
    }
    return newRedlineArray;
  }

  public undoRedlineById(fieldId: Guid): FieldRedlineArray<T> {
    const newRedlineArray = this.clone();
    newRedlineArray._sessionHistory = newRedlineArray._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(this.field),
    );
    const target = newRedlineArray._redlines.find((redline) => redline.field.id?.isEqualTo(fieldId));
    if (target?.isNewlyAdded && !target.originalEntry) {
      newRedlineArray._redlines.splice(newRedlineArray._redlines.indexOf(target), 1);
      if (!target.textChangeCollection?.textChanges.length) return newRedlineArray;
      const redlineChange = new RedlineChange({
        field: this.field,
        isResolved: true,
        action: RedlineAction.Remove,
        textChanges: target.textChangeCollection.textChanges,
      });
      this._sessionHistory.push(redlineChange);
    } else if (target) {
      const index = newRedlineArray._redlines.indexOf(target);
      newRedlineArray._redlines[index] = target.undo();
    }
    return newRedlineArray;
  }

  public updateTemplateIds(templateIds: Guid[]): FieldRedlineArray<T> {
    const newRedlineArray = this.clone();
    newRedlineArray._templateIds = templateIds;
    return newRedlineArray;
  }

  public isEqualTo(other: FieldRedlineArray<T>): boolean {
    if (!other) return false;
    if (!this.field.isEqualTo(other.field)) return false;
    if (this._redlines.length !== other._redlines.length) return false;
    if (this.templateIds.length !== other.templateIds.length) return false;
    return (
      _.every(this._redlines, (redline) => other._redlines.some((otherRedline) => redline.isEqualTo(otherRedline))) &&
      _.every(this.templateIds, (id) => other.templateIds.some((otherId) => id.isEqualTo(otherId)))
    );
  }

  protected _templateIds: Guid[] = [];
  protected _sessionHistory: RedlineChange[] = [];
  protected _redlines: FieldRedline<T>[] = [];

  private clone(): FieldRedlineArray<T> {
    return new FieldRedlineArray<T>({
      field: this.field,
      entryField: this.entryField,
      redlines: [...this._redlines],
      sessionHistory: [...this._sessionHistory],
      options: { diffWords: this.diffWords },
    });
  }
}

export type JSONType = string | number | boolean | object | null | undefined;
export interface IRedlineableField {
  isEmpty: boolean;
  toJSON(): JSONType;
  replacesId?: Guid;
  isEqualTo(other: unknown): boolean;
}

export interface IComparableFieldEntryWithId extends IRedlineableField {
  id: Guid;
}
